import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { linkWithPopup } from 'firebase/auth';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { auth } from '../firebase';
import {
  createGoogleAuthProvider,
  createMicrosoftAuthProvider,
  describeAuthError,
  getLinkedProviderIds,
} from '../utils/federatedAuth';

const METHOD_ROWS = [
  {
    id: 'password',
    label: 'Email and password',
    description: 'Use your Alphamizer email address and password.',
  },
  {
    id: 'google.com',
    label: 'Google',
    description: 'Use the selected Google account to sign in to this same Alphamizer account.',
  },
  {
    id: 'microsoft.com',
    label: 'Microsoft',
    description: 'Use the selected Microsoft work or school account to sign in to this same Alphamizer account.',
  },
];

const providerFactory = (providerId) => {
  if (providerId === 'google.com') return createGoogleAuthProvider();
  if (providerId === 'microsoft.com') return createMicrosoftAuthProvider();
  throw new Error('Unsupported sign-in method.');
};

const SignInMethodsSettings = ({
  user,
  providerIds,
  onOpenPassword,
  onProvidersChanged,
}) => {
  const [pendingProviderId, setPendingProviderId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const linkedProviderIds = new Set(providerIds);

  const handleLinkProvider = async (providerId) => {
    if (pendingProviderId) return;

    setError('');
    setSuccess('');
    setPendingProviderId(providerId);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !user?.uid || currentUser.uid !== user.uid) {
        throw Object.assign(new Error('Authentication session changed.'), {
          code: 'auth/user-mismatch',
        });
      }

      const result = await linkWithPopup(currentUser, providerFactory(providerId));
      if (
        !result?.user
        || result.user.uid !== currentUser.uid
        || auth.currentUser?.uid !== currentUser.uid
      ) {
        throw Object.assign(new Error('Authentication session changed.'), {
          code: 'auth/user-mismatch',
        });
      }

      await result.user.reload();
      const nextProviderIds = getLinkedProviderIds(result.user);
      onProvidersChanged(nextProviderIds);
      const method = METHOD_ROWS.find((row) => row.id === providerId)?.label || 'Sign-in method';
      setSuccess(`${method} is now connected.`);
    } catch (linkError) {
      setError(describeAuthError(linkError));
    } finally {
      setPendingProviderId('');
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        px: { xs: 1.4, md: 1.8 },
        py: 1.35,
      }}
    >
      <Alert severity="info" variant="outlined">
        Connecting a provider grants the selected external account access to this Alphamizer account.
        The provider email does not need to match your current email, so connect only an account you control.
      </Alert>

      {METHOD_ROWS.map((method) => {
        const connected = linkedProviderIds.has(method.id);
        const pending = pendingProviderId === method.id;
        return (
          <Box
            key={method.id}
            data-testid={`sign-in-method-${method.id}`}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                {method.label}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
                {method.description}
              </Typography>
            </Box>

            {connected ? (
              <Chip size="small" color="success" variant="outlined" label="Connected" />
            ) : (
              <Button
                size="small"
                variant="outlined"
                disabled={Boolean(pendingProviderId)}
                onClick={method.id === 'password'
                  ? onOpenPassword
                  : () => handleLinkProvider(method.id)}
              >
                {pending ? <CircularProgress size={16} /> : method.id === 'password' ? 'Set password' : 'Connect'}
              </Button>
            )}
          </Box>
        );
      })}

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
    </Box>
  );
};

SignInMethodsSettings.propTypes = {
  user: PropTypes.shape({ uid: PropTypes.string }).isRequired,
  providerIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onOpenPassword: PropTypes.func.isRequired,
  onProvidersChanged: PropTypes.func.isRequired,
};

export default SignInMethodsSettings;
